/* ==========================================================================
   Bundle Builder Web Components
   ========================================================================== */

import { trackLiterboxSelected, trackVariantSelected, trackSizeUpgraded, trackAddToCart } from './literbokse-tracking.js';

const PAGINATE_LIMIT = 48;

let _translations = {};

function t(key, vars) {
  let str = _translations[key] || key;
  if (vars) {
    for (const prop in vars) {
      str = str.replace(`{{ ${prop} }}`, vars[prop]);
    }
  }
  return str;
}

function formatMoney(cents) {
  return new Intl.NumberFormat(Shopify.locale, {
    style: 'currency',
    currency: Shopify.currency.active
  }).format(cents / 100);
}

function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    const sectionId = this.getAttribute('section-id');

    const dataEl = document.getElementById(`bundle-data-${sectionId}`);
    const transEl = document.getElementById(`bundle-translations-${sectionId}`);

    if (!dataEl) return;

    this._data = JSON.parse(dataEl.textContent);
    _translations = transEl ? JSON.parse(transEl.textContent) : {};

    this._items = [];
    this._paginateIndex = 1;
    this._addingToCart = false;
    this._cartError = '';
    this._cartLocation = null;

    this._products = this._data.products || [];
    this._settings = this._data.settings || {};
    this._shopSettings = this._data.shopSettings || {};
    this._forceHighlightedSelection = this._data.forceHighlightedSelection || false;
    this._upgrade = this._data.upgrade || null;
    this._upsellDisplayMode = this._data.upsellDisplayMode || 'popup';

    this._initHighlightedEntries();
    this._initMetafieldFilters();
    this._cacheElements();
    this._bindEvents();
    this._applyPagination();
    this._updateDisabledState();
    this._updateSummary();
    this._fetchCartLocation();
    this._preselect();

    if (this._upsellDisplayMode === 'step') {
      if (this._addToCartBtn) {
        const stepText = this._addToCartBtn.querySelector('.button-text');
        if (stepText) stepText.textContent = t('add_to_cart_step');
      }
      if (this._step2El) {
        this._step2El.addEventListener('variant:add', (e) => {
          e.detail.blockCartDrawerOpening = true;
        }, true);
      }
      this._preloadStep2Recommendations();
    }

    trackLiterboxSelected(this._data.parent);
  }

  _preselect() {
    const preselected = this._data.preselectedVariants;
    if (!preselected || preselected.length === 0) return;

    for (const variantId of preselected) {
      if (this.bundleComplete) break;
      const product = this._products.find(p =>
        p.bundleVariants.some(v => v.id === variantId)
      );
      if (!product) continue;
      const variant = product.bundleVariants.find(v => v.id === variantId);
      if (!variant || !variant.available) continue;
      this._items.push({ product, variant, highlightedFilter: null });
    }

    this._updateSummary();
    this._updateAllProductItems();
  }

  _initHighlightedEntries() {
    this._highlightedEntries = [];
    const seen = new Set();
    for (const product of this._products) {
      if (!product.highlightedEntries) continue;
      for (const entry of product.highlightedEntries) {
        if (!seen.has(entry.name)) {
          seen.add(entry.name);
          this._highlightedEntries.push({ ...entry, selected: false });
        }
      }
    }
  }

  _initMetafieldFilters() {
    const filterDefs = this._shopSettings.metafieldFilters || [];
    this._metafieldFilters = filterDefs.map(filter => {
      const values = new Set();
      for (const product of this._products) {
        if (!product.metafields) continue;
        const mf = product.metafields.find(m => m.namespace === filter.namespace && m.key === filter.key);
        if (mf && mf.value != null) {
          if (Array.isArray(mf.value)) {
            mf.value.forEach(v => { if (v != null) values.add(v); });
          } else {
            values.add(mf.value);
          }
        }
      }
      return { ...filter, values: [...values], selected: [] };
    });
  }

  _cacheElements() {
    this._gridEl = this.querySelector('[data-grid]');
    this._summaryEl = this.querySelector('[data-summary]');
    this._summaryItemsEl = this.querySelector('[data-summary-items]');
    this._summaryStatusEl = this.querySelector('[data-summary-status]');
    this._summaryPriceEl = this.querySelector('[data-summary-price]');
    this._summaryTitleEl = this.querySelector('.bundle-builder__summary-title');
    this._subtitleBadgeEl = this.querySelector('.bundle-builder__subtitle-badge');
    this._summaryLocationEl = this.querySelector('[data-summary-location]');
    this._summaryLocationMobileEl = this.querySelector('[data-summary-location-mobile]');
    this._summaryUpsellEl = this.querySelector('[data-summary-upsell]');
    this._upsellTextEl = this.querySelector('[data-upsell-text]');
    this._upsellBtnEl = this.querySelector('[data-upsell-btn]');
    this._upsellBtnTextEl = this.querySelector('[data-upsell-btn-text]');
    this._addToCartBtn = this.querySelector('[data-add-to-cart]');
    this._cartErrorEl = this.querySelector('[data-cart-error]');
    this._paginationEl = this.querySelector('[data-pagination]');
    this._showMoreBtn = this.querySelector('[data-show-more]');
    this._locationsEl = this.querySelector('[data-locations]');
    this._infoModal = document.getElementById('bundle-info-modal');
    this._modalTitleEl = this._infoModal?.querySelector('[data-modal-title]');
    this._modalDescriptionEl = this._infoModal?.querySelector('[data-modal-description]');
    this._modalAllergensEl = this._infoModal?.querySelector('[data-modal-allergens]');
    this._modalCloseBtn = this._infoModal?.querySelector('[data-modal-close]');
    this._summaryToggleEl = this.querySelector('[data-summary-toggle]');
    this._summaryChevronEl = this.querySelector('[data-summary-chevron]');
    this._productItems = [...this.querySelectorAll('bundle-product-item')];
    this._stepsEl = this.querySelector('[data-bundle-steps]');
    this._step2El = this.querySelector('[data-bundle-step-2]');
    this._headingEl = this.querySelector('.bundle-builder__heading');
    this._descriptionEl = this.querySelector('.bundle-builder__description');
    this._mainEl = this.querySelector('.bundle-builder__main');
  }

  _bindEvents() {
    this.addEventListener('click', (e) => {
      const locationCard = e.target.closest('.bundle-builder__location');
      if (locationCard) {
        this._onLocationClick(locationCard);
        return;
      }

      if (e.target.closest('[data-show-more]')) {
        this._paginateIndex++;
        this._applyPagination();
        return;
      }

      if (e.target.closest('[data-add-to-cart]')) {
        this._addToCart();
        return;
      }

      const removeBtn = e.target.closest('[data-summary-remove]');
      if (removeBtn) {
        const index = parseInt(removeBtn.dataset.summaryRemove, 10);
        this._removeFromBundle(index);
        return;
      }

      const tooltipToggle = e.target.closest('[data-tooltip-toggle]');
      if (tooltipToggle) {
        this._openInfoModal(tooltipToggle.dataset.productId);
        return;
      }

      // Also open the tooltip when clicking the product info area or the
      // image, as long as that card actually has a tooltip toggle. This
      // includes the product title link, so prevent its default navigation.
      const tooltipTrigger = e.target.closest('.bundle-builder__product-info, .bundle-builder__product-image-wrap');
      if (tooltipTrigger) {
        const card = tooltipTrigger.closest('bundle-product-item');
        const toggle = card && card.querySelector('[data-tooltip-toggle]');
        if (toggle) {
          e.preventDefault();
          this._openInfoModal(toggle.dataset.productId);
          return;
        }
      }

      const upsellBtn = e.target.closest('[data-upsell-btn]');
      if (upsellBtn) {
        e.preventDefault();
        this._performUpgrade();
        return;
      }
    });

    if (this._infoModal) {
      if (this._modalCloseBtn) {
        this._modalCloseBtn.addEventListener('click', () => this._infoModal.close());
      }
      this._infoModal.addEventListener('click', (e) => {
        if (e.target === this._infoModal) this._infoModal.close();
      });
    }

    if (this._summaryToggleEl) {
      this._summaryToggleEl.addEventListener('click', () => {
        if (this._summaryEl) {
          this._summaryEl.classList.toggle('summary-expanded');
        }
      });
    }

    this.addEventListener('bundle-product-item:add', (e) => {
      this._addToBundle(e.detail.product, e.detail.variant);
    });

    this.addEventListener('bundle-product-item:remove', (e) => {
      const index = this._items.findIndex(item => item.variant.id === e.detail.variant.id);
      if (index !== -1) this._removeFromBundle(index);
    });
  }

  get filteredProducts() {
    const noFiltersActive =
      this._metafieldFilters.every(f => f.selected.length === 0) &&
      this._highlightedEntries.every(e => !e.selected);

    if (noFiltersActive) return this._products;

    return this._products.filter(product => {
      const metafieldPass = this._metafieldFilters.every(filter => {
        if (filter.selected.length === 0) return true;
        const mf = (product.metafields || []).find(m => m.key === filter.key && m.namespace === filter.namespace);
        if (mf && mf.value != null) {
          if (Array.isArray(mf.value)) {
            return filter.selected.every(v => mf.value.includes(v));
          }
          return filter.selected.includes(mf.value);
        }
        return false;
      });

      const highlightedPass = this._highlightedEntries.every(entry => {
        if (!entry.selected) return true;
        return (product.highlightedEntries || []).some(e => e.name === entry.name);
      });

      return metafieldPass && highlightedPass;
    });
  }

  get bundleComplete() {
    return this._items.length === (this._settings.quantity || 0);
  }

  get hasHighlightedFilterSelected() {
    return this._highlightedEntries.some(e => e.selected);
  }

  _onLocationClick(card) {
    const name = card.dataset.locationName;
    const wasAnySelected = this.hasHighlightedFilterSelected;

    this._highlightedEntries.forEach(entry => {
      entry.selected = entry.name === name ? !entry.selected : false;
    });

    if (this._locationsEl) {
      this._locationsEl.querySelectorAll('.bundle-builder__location').forEach(el => {
        const isSelected = this._highlightedEntries.find(e => e.name === el.dataset.locationName)?.selected;
        el.classList.toggle('selected', !!isSelected);
      });
    }

    this._paginateIndex = 1;
    this._applyPagination();
    this._updateDisabledState();

    if (this.hasHighlightedFilterSelected && !wasAnySelected && this._forceHighlightedSelection) {
      setTimeout(() => {
        const mainEl = this.querySelector('.bundle-builder__main');
        if (mainEl) mainEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }

  _applyPagination() {
    const filtered = this.filteredProducts;
    const filteredIds = new Set(filtered.map(p => String(p.id)));
    const visibleLimit = this._paginateIndex * PAGINATE_LIMIT;
    let visibleCount = 0;

    this._productItems.forEach(el => {
      const productId = el.dataset.productId;
      if (filteredIds.has(productId)) {
        visibleCount++;
        el.hidden = visibleCount > visibleLimit;
      } else {
        el.hidden = true;
      }
    });

    if (this._paginationEl) {
      const hasMore = filtered.length > visibleLimit;
      this._paginationEl.hidden = !hasMore;
      if (this._showMoreBtn && hasMore) {
        this._showMoreBtn.textContent = t('show_more', {
          count: Math.min(visibleLimit, filtered.length),
          total_count: filtered.length
        });
      }
    }
  }

  _updateDisabledState() {
    this._updateAllProductItems();
  }

  _addToBundle(product, variant) {
    if (this.bundleComplete) return;

    const currentHighlighted = this._highlightedEntries.find(e => e.selected);
    this._items.push({
      product,
      variant,
      highlightedFilter: currentHighlighted ? currentHighlighted.id : null
    });

    trackVariantSelected(product.title, this._items.filter(item => item.variant.id === variant.id).length);

    this._updateSummary();
    this._updateAllProductItems();
  }

  _removeFromBundle(index) {
    const removed = this._items[index];
    this._items.splice(index, 1);

    if (removed) {
      trackVariantSelected(removed.product.title, this._items.filter(item => item.variant.id === removed.variant.id).length);
    }

    this._updateSummary();
    this._updateAllProductItems();
  }

  _updateAllProductItems() {
    const complete = this.bundleComplete;

    this._productItems.forEach(el => {
      if (el.updateBundleState) {
        const selectedVariantId = el._getSelectedVariantId ? el._getSelectedVariantId() : null;
        const count = this._items.filter(item => item.variant.id === selectedVariantId).length;
        el.updateBundleState(count, complete);
      }
    });
  }

  _updateSummary() {
    const count = this._items.length;
    const total = this._settings.quantity || 0;
    const isFull = this.bundleComplete;

    if (this._summaryEl) {
      const wasFull = this._summaryEl.classList.contains('bundle-full');
      this._summaryEl.classList.toggle('bundle-full', isFull);
      this._summaryEl.classList.toggle('has-selections', count > 0);
      if (count === 0) {
        this._summaryEl.classList.remove('summary-expanded');
      } else if (isFull && !wasFull) {
        this._summaryEl.classList.add('summary-expanded');
      }
    }

    if (this._summaryItemsEl) {
      if (count === 0) {
        this._summaryItemsEl.innerHTML = '';
        this._summaryItemsEl.classList.remove('has-items');
      } else {
        this._summaryItemsEl.classList.add('has-items');
        this._summaryItemsEl.innerHTML = this._items.map((item, index) => {
          const imgSrc = item.product.featuredMedia?.src || '';
          const showVariantTitle = item.product.bundleVariants && item.product.bundleVariants.length > 1;

          return `
            <div class="bundle-builder__summary-item" draggable="true" data-drag-index="${index}">
              <span class="bundle-builder__summary-item-drag" data-drag-handle>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><g opacity="0.5"><path d="M9.54545 7.27278C9.79649 7.27278 10 7.47629 10 7.72733C10 7.97837 9.79649 8.18187 9.54545 8.18187H0.454545C0.203507 8.18187 0 7.97837 0 7.72733C0 7.47629 0.203507 7.27278 0.454545 7.27278H9.54545ZM9.54545 4.54551C9.79649 4.54551 10 4.74902 10 5.00006C10 5.25109 9.79649 5.4546 9.54545 5.4546H0.454545C0.203507 5.4546 0 5.25109 0 5.00006C0 4.74902 0.203507 4.54551 0.454545 4.54551H9.54545ZM9.54545 1.81824C9.79649 1.81824 10 2.02174 10 2.27278C10 2.52382 9.79649 2.72733 9.54545 2.72733H0.454545C0.203507 2.72733 0 2.52382 0 2.27278C0 2.02174 0.203507 1.81824 0.454545 1.81824H9.54545Z" fill="#26403A"/></g></svg>
              </span>
              ${imgSrc ? `<img src="${imgSrc}" alt="${item.product.title}" class="bundle-builder__summary-item-image" loading="lazy">` : ''}
              <div style="flex: 1; min-width: 0">
                <p class="bundle-builder__summary-item-title">${item.product.title}</p>
                ${showVariantTitle ? `<p class="bundle-builder__summary-item-variant">${item.variant.title}</p>` : ''}
              </div>
              <button class="bundle-builder__summary-item-remove" data-summary-remove="${index}" aria-label="Remove">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><g opacity="0.5"><path d="M5.83331 6.41663V9.91663" stroke="#26403A" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.16669 6.41663V9.91663" stroke="#26403A" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.0834 3.5V11.6667C11.0834 11.9761 10.9604 12.2728 10.7416 12.4916C10.5229 12.7104 10.2261 12.8333 9.91669 12.8333H4.08335C3.77393 12.8333 3.47719 12.7104 3.2584 12.4916C3.0396 12.2728 2.91669 11.9761 2.91669 11.6667V3.5" stroke="#26403A" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.75 3.5H12.25" stroke="#26403A" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.66669 3.49996V2.33329C4.66669 2.02387 4.7896 1.72713 5.0084 1.50833C5.22719 1.28954 5.52393 1.16663 5.83335 1.16663H8.16669C8.47611 1.16663 8.77285 1.28954 8.99164 1.50833C9.21044 1.72713 9.33335 2.02387 9.33335 2.33329V3.49996" stroke="#26403A" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/></g></svg>
              </button>
            </div>
          `;
        }).join('');

        this._initDragAndDrop();
      }
    }

    if (this._summaryStatusEl) {
      if (isFull) {
        this._summaryStatusEl.textContent = t('box_full');
        this._summaryStatusEl.classList.add('bundle-builder__summary-status--full');
      } else {
        const remaining = total - count;
        this._summaryStatusEl.innerHTML = `<strong>${count}/${total}</strong> ${t('flavors_selected_label')} - ${t('choose_more', { remaining })}`;
        this._summaryStatusEl.classList.remove('bundle-builder__summary-status--full');
      }
    }

    if (this._summaryPriceEl) {
      this._summaryPriceEl.textContent = formatMoney(this._data.parent.price);
    }

    if (this._summaryUpsellEl) {
      if (isFull && this._upgrade) {
        this._summaryUpsellEl.hidden = false;
        if (this._upsellTextEl) this._upsellTextEl.textContent = t('upgrade_prompt');
        if (this._upsellBtnEl) {
          const priceDiff = formatMoney(this._upgrade.priceDifference);
          const label = this._upgrade.variantTitle || this._upgrade.title;
          const btnTextEl = this._upsellBtnTextEl || this._upsellBtnEl;
          btnTextEl.textContent = `${t('upgrade_button', { title: label })} - +${priceDiff}`;
          this._upsellBtnEl.href = this._upgrade.url;
        }
      } else {
        this._summaryUpsellEl.hidden = true;
      }
    }

    if (this._addToCartBtn) {
      this._addToCartBtn.disabled = !isFull;
      this._addToCartBtn.textContent = t('add_to_cart');
    }

    if (this._cartErrorEl) {
      this._cartErrorEl.hidden = true;
      this._cartErrorEl.textContent = '';
    }
  }

  async _fetchCartLocation() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await response.json();
      const location = cart.attributes?.location || 'Kødbyen';
      this._cartLocation = location;
      this._renderLocation(location);

      for (const entry of this._highlightedEntries) {
        entry.selected = entry.name === location;
      }
      if (this._locationsEl) {
        this._locationsEl.querySelectorAll('.bundle-builder__location').forEach(el => {
          el.classList.toggle('selected', el.dataset.locationName === location);
        });
      }
      if (this.hasHighlightedFilterSelected) {
        this._paginateIndex = 1;
        this._applyPagination();
        this._updateDisabledState();
      }
    } catch (_) {}
  }

  _renderLocation(location) {
    const html = `${t('ordered_for')} <strong>${location}</strong>`;
    if (this._summaryLocationEl) {
      this._summaryLocationEl.innerHTML = html;
      this._summaryLocationEl.hidden = false;
    }
    if (this._summaryLocationMobileEl) {
      this._summaryLocationMobileEl.innerHTML = html;
      this._summaryLocationMobileEl.hidden = false;
    }
  }

  _openInfoModal(productId) {
    if (!this._infoModal) return;
    const product = this._products.find(p => String(p.id) === String(productId));
    if (!product) return;

    if (this._modalTitleEl) this._modalTitleEl.textContent = product.title;

    if (this._modalDescriptionEl) {
      this._modalDescriptionEl.innerHTML = product.description || '';
    }

    if (this._modalAllergensEl) {
      if (product.tooltip) {
        this._modalAllergensEl.innerHTML = `<p class="bundle-builder__modal-allergens-label">${t('allergens')}</p>${product.tooltip}`;
        this._modalAllergensEl.hidden = false;
      } else {
        this._modalAllergensEl.innerHTML = '';
        this._modalAllergensEl.hidden = true;
      }
    }

    this._infoModal.showModal();
  }

  /* ---------- Drag & Drop ---------- */

  _initDragAndDrop() {
    if (!this._summaryItemsEl) return;
    const items = this._summaryItemsEl.querySelectorAll('.bundle-builder__summary-item');

    items.forEach(item => {
      item.addEventListener('dragstart', (e) => this._onDragStart(e));
      item.addEventListener('dragover', (e) => this._onDragOver(e));
      item.addEventListener('dragleave', (e) => this._onDragLeave(e));
      item.addEventListener('drop', (e) => this._onDrop(e));
      item.addEventListener('dragend', (e) => this._onDragEnd(e));

      item.addEventListener('touchstart', (e) => {
        if (e.target.closest('[data-summary-remove]')) return;
        this._onTouchHoldStart(e, item);
      }, { passive: false });
    });
  }

  _onDragStart(e) {
    const item = e.target.closest('.bundle-builder__summary-item');
    if (!item) return;
    this._dragIndex = parseInt(item.dataset.dragIndex, 10);
    item.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
  }

  _onDragOver(e) {
    e.preventDefault();
    const item = e.target.closest('.bundle-builder__summary-item');
    if (item) item.classList.add('drag-over');
  }

  _onDragLeave(e) {
    const item = e.target.closest('.bundle-builder__summary-item');
    if (item) item.classList.remove('drag-over');
  }

  _onDrop(e) {
    e.preventDefault();
    const item = e.target.closest('.bundle-builder__summary-item');
    if (!item) return;
    item.classList.remove('drag-over');

    const dropIndex = parseInt(item.dataset.dragIndex, 10);
    if (this._dragIndex !== undefined && this._dragIndex !== dropIndex) {
      const moved = this._items.splice(this._dragIndex, 1)[0];
      this._items.splice(dropIndex, 0, moved);
      this._updateSummary();
    }
  }

  _onDragEnd(e) {
    const item = e.target.closest('.bundle-builder__summary-item');
    if (item) item.classList.remove('dragging');
    this._dragIndex = undefined;
  }

  _onTouchHoldStart(e, item) {
    const HOLD_DELAY = 150;
    const startTouch = e.touches[0];
    let holdTimer = null;
    let cancelled = false;

    item.classList.add('drag-holding');

    const cancelHold = () => {
      cancelled = true;
      clearTimeout(holdTimer);
      item.classList.remove('drag-holding');
      document.removeEventListener('touchmove', onEarlyMove);
      document.removeEventListener('touchend', onEarlyEnd);
    };

    const onEarlyMove = (moveE) => {
      const touch = moveE.touches[0];
      const dx = Math.abs(touch.clientX - startTouch.clientX);
      const dy = Math.abs(touch.clientY - startTouch.clientY);
      if (dx > 10 || dy > 10) cancelHold();
    };

    const onEarlyEnd = () => cancelHold();

    document.addEventListener('touchmove', onEarlyMove, { passive: true });
    document.addEventListener('touchend', onEarlyEnd);

    holdTimer = setTimeout(() => {
      if (cancelled) return;
      document.removeEventListener('touchmove', onEarlyMove);
      document.removeEventListener('touchend', onEarlyEnd);
      this._startTouchDrag(e, item);
    }, HOLD_DELAY);
  }

  _startTouchDrag(e, item) {
    e.preventDefault();
    const index = parseInt(item.dataset.dragIndex, 10);
    const rect = item.getBoundingClientRect();
    const clone = item.cloneNode(true);

    clone.style.position = 'fixed';
    clone.style.width = `${rect.width}px`;
    clone.style.left = `${rect.left}px`;
    clone.style.top = `${rect.top}px`;
    clone.style.opacity = '0.85';
    clone.style.zIndex = '100';
    clone.style.pointerEvents = 'none';
    clone.style.transition = 'none';
    clone.style.transform = 'scale(1.03)';
    clone.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    clone.style.borderRadius = 'var(--rounded-sm, 4px)';
    clone.style.background = '#fff';
    document.body.appendChild(clone);

    item.classList.remove('drag-holding');
    item.classList.add('dragging');

    const onTouchMove = (moveE) => {
      moveE.preventDefault();
      const touch = moveE.touches[0];
      clone.style.top = `${touch.clientY - rect.height / 2}px`;

      const hoverTarget = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('.bundle-builder__summary-item');
      for (const el of this._summaryItemsEl.querySelectorAll('.drag-over')) {
        el.classList.remove('drag-over');
      }
      if (hoverTarget && hoverTarget !== item) {
        hoverTarget.classList.add('drag-over');
      }
    };

    const onTouchEnd = (endE) => {
      const touch = endE.changedTouches[0];
      clone.remove();
      item.classList.remove('dragging');

      for (const el of this._summaryItemsEl.querySelectorAll('.drag-over')) {
        el.classList.remove('drag-over');
      }

      const dropTarget = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('.bundle-builder__summary-item');
      if (dropTarget) {
        const dropIndex = parseInt(dropTarget.dataset.dragIndex, 10);
        if (index !== dropIndex) {
          const moved = this._items.splice(index, 1)[0];
          this._items.splice(dropIndex, 0, moved);
          this._updateSummary();
        }
      }

      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  }

  /* ---------- Upsell upgrade ---------- */

  async _performUpgrade() {
    if (!this._upgrade) return;

    trackSizeUpgraded(this._data.parent, this._upgrade);

    try {
      const response = await fetch(this._upgrade.url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newDataEl = doc.querySelector('script[id^="bundle-data-"]');
      const newTransEl = doc.querySelector('script[id^="bundle-translations-"]');
      const newGridEl = doc.querySelector('[data-grid]');

      if (!newDataEl) return;

      const newData = JSON.parse(newDataEl.textContent);
      if (newTransEl) _translations = JSON.parse(newTransEl.textContent);

      const prevItems = [...this._items];

      this._data = newData;
      this._products = newData.products || [];
      this._settings = newData.settings || {};
      this._shopSettings = newData.shopSettings || {};
      this._upgrade = newData.upgrade || null;

      this._items = [];
      for (const prevItem of prevItems) {
        const matchProduct = this._products.find(p => p.id === prevItem.product.id);
        if (matchProduct) {
          const matchVariant = matchProduct.bundleVariants.find(v => v.id === prevItem.variant.id);
          if (matchVariant) {
            this._items.push({
              product: matchProduct,
              variant: matchVariant,
              highlightedFilter: prevItem.highlightedFilter
            });
          }
        }
      }

      if (newGridEl && this._gridEl) {
        this._gridEl.innerHTML = newGridEl.innerHTML;
        this._productItems = [...this.querySelectorAll('bundle-product-item')];
        this._productItems.forEach(el => {
          if (typeof el.connectedCallback === 'function' && !el._addBtn) {
            el.connectedCallback();
          }
        });
      }

      const newHandle = newData.parent?.handle || this._upgrade.handle;
      if (newHandle) {
        history.pushState({}, '', `${Shopify.routes.root}products/${newHandle}`);
      }

      const prevSelectedHighlighted = this._highlightedEntries.find(e => e.selected)?.name;
      this._initHighlightedEntries();
      this._initMetafieldFilters();

      if (prevSelectedHighlighted) {
        const match = this._highlightedEntries.find(e => e.name === prevSelectedHighlighted);
        if (match) {
          match.selected = true;
          if (this._locationsEl) {
            this._locationsEl.querySelectorAll('.bundle-builder__location').forEach(el => {
              el.classList.toggle('selected', el.dataset.locationName === prevSelectedHighlighted);
            });
          }
        }
      }

      const newTitle = newData.parent?.title;
      if (newTitle) {
        if (this._summaryTitleEl) this._summaryTitleEl.textContent = newTitle;
        if (this._subtitleBadgeEl) this._subtitleBadgeEl.textContent = newTitle;
      }

      this._applyPagination();
      this._updateSummary();
      this._updateAllProductItems();
      this._updateDisabledState();

    } catch (err) {
      window.location.href = this._upgrade.url;
    }
  }

  /* ---------- Add to cart ---------- */

  async _addToCart() {
    if (this._addingToCart || !this.bundleComplete) return;
    this._addingToCart = true;

    trackAddToCart(this._data.parent);

    if (this._addToCartBtn) {
      this._addToCartBtn.innerHTML = `${t('add_to_cart')}<svg class="spinner" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.59C100 78.2 77.61 100.59 50 100.59S0 78.2 0 50.59 22.39.59 50 .59s50 22.39 50 50Z" fill="currentColor" opacity=".3"/><path d="M93.97 39.04c2.42-.64 3.89-3.13 3.04-5.49A50.003 50.003 0 0 0 41.73 1.28c-2.47.41-3.92 2.92-3.28 5.35.64 2.42 3.12 3.85 5.6 3.49a40.1 40.1 0 0 1 44.42 22.85c.9 2.24 3.36 3.7 5.79 3.07h-.29Z" fill="white"/></svg>`;
    }

    try {
      const selectedHighlighted = this._highlightedEntries.find(e => e.selected);
      const properties = {
        _bundle_uuid: uuid(),
        _bundle_variant_ids: this._items.map(item => item.variant.id).join(','),
        _bundle_version: new Date().toISOString().split('T')[0] + '_v2'
      };


      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: this._data.parent.variantId,
            quantity: 1,
            properties
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'An error occurred. Please try again.');
      }

      const responseJson = await response.json();
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cartContent = await cartResponse.json();

      if (this._upsellDisplayMode === 'step') {
        this._showStep2();
      } else {
        document.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
            cart: cartContent,
            blockCartDrawerOpening: true,
            source: 'bundle-builder'
          }
        }));
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));

    } catch (error) {
      this._cartError = error.message;
      if (this._cartErrorEl) {
        this._cartErrorEl.textContent = this._cartError;
        this._cartErrorEl.hidden = false;
      }
    } finally {
      this._addingToCart = false;
      if (this._addToCartBtn) {
        const btnKey = this._upsellDisplayMode === 'step' ? 'add_to_cart_step' : 'add_to_cart';
        this._addToCartBtn.textContent = t(btnKey);
      }
    }
  }

  _showStep2() {
    if (this._headingEl) this._headingEl.hidden = true;
    if (this._descriptionEl) this._descriptionEl.hidden = true;
    if (this._mainEl) this._mainEl.hidden = true;
    if (this._step2El) this._step2El.hidden = false;

    if (this._stepsEl) {
      const step1 = this._stepsEl.querySelector('[data-step="1"]');
      const step2 = this._stepsEl.querySelector('[data-step="2"]');
      const line1 = this._stepsEl.querySelector('[data-step-line="1"]');
      if (step1) step1.classList.remove('bundle-steps__step--active');
      if (step2) step2.classList.add('bundle-steps__step--active');
      if (line1) line1.classList.add('bundle-steps__line--active');
    }

    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this._renderStep2Cards();
  }

  async _preloadStep2Recommendations() {
    const productId = this._data.parent.id;
    try {
      const res = await fetch(
        `/recommendations/products?product_id=${productId}&limit=9&intent=related&section_id=bundle-step-2-recs`
      );
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this._step2Cards = [...doc.querySelectorAll('.bundle-step-2__card')];
    } catch {
      this._step2Cards = [];
    }
  }

  _renderStep2Cards() {
    const grid = this.querySelector('[data-step-2-grid]');
    if (!grid || !this._step2Cards) return;

    if (this._step2Cards.length === 0) {
      window.location.href = '/cart';
      return;
    }

    grid.innerHTML = '';
    for (const card of this._step2Cards) {
      grid.appendChild(document.adoptNode(card));
    }
  }
}

class BundleProductItem extends HTMLElement {
  connectedCallback() {
    this._addBtn = this.querySelector('[data-add-btn]');
    this._addMoreBtn = this.querySelector('[data-add-more-btn]');
    this._removeBtn = this.querySelector('[data-remove-btn]');
    this._quantityControl = this.querySelector('[data-quantity-control]');
    this._countEl = this.querySelector('[data-count]');
    this._variantSelect = this.querySelector('[data-variant-select]');

    this._count = 0;
    this._complete = false;

    this._bindEvents();
  }

  _bindEvents() {
    if (this._addBtn) {
      this._addBtn.addEventListener('click', () => this._emitAdd());
    }

    if (this._addMoreBtn) {
      this._addMoreBtn.addEventListener('click', () => this._emitAdd());
    }

    if (this._removeBtn) {
      this._removeBtn.addEventListener('click', () => this._emitRemove());
    }

    if (this._variantSelect) {
      this._variantSelect.addEventListener('change', () => {
        this._requestCountUpdate();
      });
    }
  }

  _getSelectedVariant() {
    if (this._variantSelect) {
      const option = this._variantSelect.selectedOptions[0];
      return {
        id: parseInt(option.value, 10),
        title: option.textContent.trim(),
        available: option.dataset.available === 'true',
        inventory_policy: option.dataset.inventoryPolicy,
        inventory_management: option.dataset.inventoryManagement,
        inventory_quantity: parseInt(option.dataset.inventoryQuantity, 10) || 0
      };
    }

    const parent = this.closest('bundle-builder');
    if (!parent || !parent._data) return null;

    const productId = parseInt(this.dataset.productId, 10);
    const product = parent._products.find(p => p.id === productId);
    if (product && product.bundleVariants && product.bundleVariants.length > 0) {
      return product.bundleVariants[0];
    }
    return null;
  }

  _getSelectedVariantId() {
    const variant = this._getSelectedVariant();
    return variant ? variant.id : null;
  }

  _getProduct() {
    const parent = this.closest('bundle-builder');
    if (!parent) return null;
    const productId = parseInt(this.dataset.productId, 10);
    return parent._products.find(p => p.id === productId);
  }

  _emitAdd() {
    const variant = this._getSelectedVariant();
    const product = this._getProduct();
    if (!variant || !product) return;

    this.dispatchEvent(new CustomEvent('bundle-product-item:add', {
      detail: { product, variant },
      bubbles: true
    }));
  }

  _emitRemove() {
    const variant = this._getSelectedVariant();
    if (!variant) return;

    this.dispatchEvent(new CustomEvent('bundle-product-item:remove', {
      detail: { variant },
      bubbles: true
    }));
  }

  _requestCountUpdate() {
    const parent = this.closest('bundle-builder');
    if (parent && parent._updateAllProductItems) {
      parent._updateAllProductItems();
    }
  }

  updateBundleState(count, complete) {
    this._count = count;
    this._complete = complete;

    const variant = this._getSelectedVariant();
    const isAvailable = variant ? variant.available : false;
    const canAddMore = this._canAddMore(variant);

    if (this._addBtn && this._quantityControl) {
      if (count === 0) {
        this._addBtn.hidden = false;
        this._quantityControl.hidden = true;
        this._addBtn.disabled = !isAvailable || complete;
        this._addBtn.classList.toggle('bundle-builder--disabled', !isAvailable || complete);
      } else {
        this._addBtn.hidden = true;
        this._quantityControl.hidden = false;
      }
    }

    if (this._countEl) {
      this._countEl.textContent = count;
    }

    if (this._addMoreBtn) {
      this._addMoreBtn.disabled = !canAddMore || complete;
    }
  }

  _canAddMore(variant) {
    if (!variant || !variant.available) return false;
    if (variant.inventory_policy === 'continue') return true;
    if (!variant.inventory_management) return true;
    return this._count < variant.inventory_quantity;
  }
}

if (!window.customElements.get('bundle-builder')) {
  window.customElements.define('bundle-builder', BundleBuilder);
}

if (!window.customElements.get('bundle-product-item')) {
  window.customElements.define('bundle-product-item', BundleProductItem);
}
